import { ApiError, apiRequest } from "./apiClient";
import type {
  KbGenerationFieldStep,
  KbGenerationTemplateDetail,
  KbGenerationTemplateListItem,
  KbGenerationTemplateListResult,
} from "../types/gameRuleKbGenerationTemplate";

type SuccessResponse<T> = {
  readonly success?: boolean;
  readonly data: T;
  readonly message?: string;
};

type TemplateListApi = {
  readonly items?: readonly {
    readonly id: string;
    readonly template_group_id: string;
    readonly name: string;
    readonly version: number;
    readonly section_count: number;
    readonly total_versions?: number;
    readonly updated_at: string;
  }[];
  readonly total?: number;
};

type TemplateDetailApi = {
  readonly id: string;
  readonly template_group_id: string;
  readonly name: string;
  readonly version: number;
  readonly section_count: number;
  readonly total_versions?: number;
  readonly section_ids?: readonly string[];
  readonly created_at: string;
  readonly updated_at: string;
};

type FieldStepApiField = {
  readonly key: string;
  readonly label: string;
  readonly type: string;
  readonly required?: boolean;
  readonly value?: string;
  readonly dropdown_options?: readonly string[] | null;
  readonly doc_placeholder?: string | null;
  readonly hint?: string | null;
};

type FieldStepApiItem = {
  readonly id: string;
  readonly name: string;
  readonly tag?: string | null;
  readonly description?: string | null;
  readonly content?: string | null;
  readonly fields?: readonly FieldStepApiField[];
};

async function parseDetail(response: Response): Promise<string> {
  const raw = await response.text();
  try {
    const parsed = JSON.parse(raw) as { detail?: string; message?: string };
    return parsed.detail ?? parsed.message ?? (raw || `HTTP ${response.status}`);
  } catch {
    return raw || `HTTP ${response.status}`;
  }
}

function parseListItem(row: {
  readonly id: string;
  readonly template_group_id: string;
  readonly name: string;
  readonly version: number;
  readonly section_count: number;
  readonly total_versions?: number;
  readonly updated_at: string;
}): KbGenerationTemplateListItem {
  return {
    id: String(row.id),
    templateGroupId: String(row.template_group_id),
    name: row.name,
    version: row.version,
    sectionCount: row.section_count,
    totalVersions: row.total_versions ?? 1,
    updatedAt: row.updated_at,
  };
}

function mapFieldStep(step: FieldStepApiItem): KbGenerationFieldStep {
  return {
    id: step.id,
    name: step.name,
    tag: step.tag ?? null,
    description: step.description ?? null,
    content: step.content ?? null,
    fields: (step.fields ?? []).map((field) => ({
      key: field.key,
      label: field.label,
      type: field.type,
      required: Boolean(field.required),
      value: field.value ?? "",
      dropdownOptions: field.dropdown_options ?? null,
      docPlaceholder: field.doc_placeholder ?? null,
      hint: field.hint ?? null,
    })),
  };
}

export type ListKbGenerationTemplatesParams = {
  readonly page: number;
  readonly limit: number;
  readonly search?: string;
};

export async function listKbGenerationTemplatesPaginated(
  params: ListKbGenerationTemplatesParams,
): Promise<KbGenerationTemplateListResult> {
  const limit = Math.max(1, Math.min(params.limit, 100));
  const page = Math.max(1, params.page);
  const offset = (page - 1) * limit;

  const query = new URLSearchParams();
  query.set("limit", String(limit));
  query.set("offset", String(offset));
  if (params.search?.trim()) {
    query.set("search", params.search.trim());
  }

  const response = await apiRequest(
    `/game-rule/kb/generate/templates?${query.toString()}`,
    undefined,
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }

  const json = (await response.json()) as SuccessResponse<TemplateListApi>;
  const data = json.data ?? { items: [], total: 0 };
  const items = Array.isArray(data.items) ? data.items.map(parseListItem) : [];
  return {
    items,
    total: data.total ?? items.length,
  };
}

export async function getKbGenerationTemplate(
  templateId: string,
): Promise<KbGenerationTemplateDetail> {
  const response = await apiRequest(
    `/game-rule/kb/generate/templates/${encodeURIComponent(templateId)}`,
    undefined,
  );
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<TemplateDetailApi>;
  const row = json.data;
  return {
    id: String(row.id),
    templateGroupId: String(row.template_group_id),
    name: row.name,
    version: row.version,
    sectionCount: row.section_count,
    totalVersions: row.total_versions ?? 1,
    sectionIds: Array.isArray(row.section_ids) ? row.section_ids.map(String) : [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function buildKbGenerationFieldSteps(
  selectedSectionIds: readonly string[],
): Promise<readonly KbGenerationFieldStep[]> {
  const response = await apiRequest("/game-rule/kb/generate/field-steps", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ selected_section_ids: selectedSectionIds }),
  });
  if (!response.ok) {
    throw new ApiError(await parseDetail(response), response.status);
  }
  const json = (await response.json()) as SuccessResponse<{
    steps?: readonly FieldStepApiItem[];
  }>;
  const steps = Array.isArray(json.data?.steps) ? json.data.steps : [];
  return steps.map(mapFieldStep);
}
